import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, CheckIcon, CreditCard, XIcon } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { 
  Transacao, 
  TipoTransacao, 
  StatusTransacao, 
  Periodicidade
} from "@/types/financeiro";

// Categorias pré-definidas
const categoriasDespesas = [
  "Água/Luz/Internet",
  "Aluguel",
  "Alimentação",
  "Educação",
  "Impostos",
  "Investimentos",
  "Lazer",
  "Saúde",
  "Transporte",
  "Outros"
];

const categoriasReceitas = [
  "Salário",
  "Investimentos",
  "Vendas",
  "Freelance",
  "Reembolsos",
  "Outros"
];

// Schema de validação
const transacaoSchema = z.object({
  descricao: z.string().min(3, "Descrição deve ter ao menos 3 caracteres"),
  valor: z.coerce.number().positive("Valor deve ser maior que zero"),
  data: z.date({ required_error: "A data é obrigatória" }),
  tipo: z.enum(["receita", "despesa"]),
  status: z.enum(["pendente", "pago", "atrasado", "cancelado"]),
  categoria: z.string().min(1, "Selecione uma categoria"),
  observacoes: z.string().optional(),
  recorrente: z.boolean().default(false),
  periodicidade: z.enum(["unica", "diaria", "semanal", "mensal", "anual"]).optional(),
  dataFim: z.date().optional(),
});

type TransacaoFormValues = z.infer<typeof transacaoSchema>;

interface CadastroTransacaoProps {
  onSave: (transacao: Omit<Transacao, "id" | "criadoEm" | "criadoPor">) => void;
  transacao?: Transacao | null;
  onCancel?: () => void;
}

const CadastroTransacao: React.FC<CadastroTransacaoProps> = ({ 
  onSave, 
  transacao = null,
  onCancel 
}) => {
  const form = useForm<TransacaoFormValues>({
    resolver: zodResolver(transacaoSchema),
    defaultValues: {
      descricao: transacao?.descricao || "",
      valor: transacao?.valor || 0,
      data: transacao?.data || new Date(),
      tipo: transacao?.tipo || "despesa",
      status: transacao?.status || "pendente",
      categoria: transacao?.categoria || "",
      observacoes: transacao?.observacoes || "",
      recorrente: transacao?.recorrente || false,
      periodicidade: transacao?.periodicidade || "unica",
      dataFim: transacao?.dataFim,
    },
  });

  const tipoAtual = form.watch("tipo");
  const isRecorrente = form.watch("recorrente");

  const handleSubmit = (data: TransacaoFormValues) => {
    const transacaoData: Omit<Transacao, "id" | "criadoEm" | "criadoPor"> = {
      descricao: data.descricao,
      valor: data.valor,
      data: data.data,
      tipo: data.tipo,
      status: data.status,
      categoria: data.categoria,
      observacoes: data.observacoes || "",
      recorrente: data.recorrente,
      periodicidade: data.recorrente ? data.periodicidade : undefined,
      dataFim: data.recorrente ? data.dataFim : undefined,
    };
    
    onSave(transacaoData);
    form.reset();
  };

  return (
    <Card className="w-full shadow-sm">
      <CardHeader className="bg-gray-50">
        <CardTitle className="text-xl flex items-center">
          <CreditCard className="h-5 w-5 mr-2" />
          {transacao ? "Editar Transação" : "Nova Transação"}
        </CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Pagamento de aluguel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="valor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor (R$)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0,00"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="data"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Data</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className="w-full flex justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "PPP", { locale: pt })
                              ) : (
                                <span>Selecione uma data</span>
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tipo"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Tipo de Transação</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="receita" id="receita" />
                            <Label htmlFor="receita" className="text-green-600">Receita</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="despesa" id="despesa" />
                            <Label htmlFor="despesa" className="text-red-600">Despesa</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="categoria"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma categoria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {tipoAtual === "receita" 
                            ? categoriasReceitas.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))
                            : categoriasDespesas.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))
                          }
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="pago">Pago</SelectItem>
                          <SelectItem value="atrasado">Atrasado</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recorrente"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Transação Recorrente</FormLabel>
                        <FormDescription>
                          Esta transação se repetirá periodicamente
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {isRecorrente && (
                  <>
                    <FormField
                      control={form.control}
                      name="periodicidade"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Periodicidade</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a periodicidade" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="diaria">Diária</SelectItem>
                              <SelectItem value="semanal">Semanal</SelectItem>
                              <SelectItem value="mensal">Mensal</SelectItem>
                              <SelectItem value="anual">Anual</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dataFim"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Data Final (opcional)</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className="w-full flex justify-start text-left font-normal"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "PPP", { locale: pt })
                                  ) : (
                                    <span>Sem data final</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <div className="p-2 border-b">
                                <Button 
                                  variant="ghost" 
                                  className="w-full justify-start text-muted-foreground"
                                  onClick={() => field.onChange(undefined)}
                                >
                                  <XIcon className="mr-2 h-4 w-4" />
                                  Limpar data
                                </Button>
                              </div>
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormDescription>
                            Define quando esta transação recorrente deixará de ser gerada
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            </div>

            <FormField
              control={form.control}
              name="observacoes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações (opcional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Informações adicionais sobre a transação"
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <Separator />
          <CardFooter className="flex justify-between pt-6">
            {onCancel && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
              >
                Cancelar
              </Button>
            )}
            <Button type="submit" className="ml-auto">
              <CheckIcon className="mr-2 h-4 w-4" />
              {transacao ? "Atualizar" : "Salvar"} Transação
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default CadastroTransacao;
